import { AnimatePresence, Reorder, motion, useDragControls } from 'framer-motion';
import type React from 'react';
import { Suspense, lazy, useCallback, useMemo, useRef, useState } from 'react';
import type { Novel } from '../data/samples';
import { useCustomNovels } from '../hooks/useCustomNovels';
import { useReaderSettings } from '../hooks/useReaderSettings';
import { type ReadingHistoryItem, useReadingHistory } from '../hooks/useReadingHistory';
import { SPRING_BOUNCY } from '../utils/animationPresets';
import { isDarkTheme } from '../utils/theme';
import { CoverConfigsRecordSchema } from '../utils/validation/storageSchemas';
import { AddToHomeScreen } from './AddToHomeScreen';
import { BookOpenIcon, PlusIcon, SettingsIcon } from './Icons';
import { type NovelTheme, ReaderSettings } from './ReaderSettings';
import { AddNovelSheet } from './bookshelf/AddNovelSheet';
import { BookshelfFilters } from './bookshelf/BookshelfFilters';
import { LatestBookmarkCard } from './bookshelf/LatestBookmarkCard';
import { NovelCard, type NovelCoverConfig } from './bookshelf/NovelCard';
import { useBookshelfFilters } from './bookshelf/useBookshelfFilters';
import { createNovelFromFile, useNovelFileImport } from './bookshelf/useNovelFileImport';

const ConfirmDialog = lazy(() =>
  import('./ConfirmDialog').then((m) => ({ default: m.ConfirmDialog })),
);
const NarouImportModal = lazy(() =>
  import('./NarouImportModal').then((m) => ({ default: m.NarouImportModal })),
);
const CoverEditorModal = lazy(() =>
  import('./bookshelf/CoverEditorModal').then((m) => ({ default: m.CoverEditorModal })),
);
const NovelDetailSheet = lazy(() =>
  import('./bookshelf/NovelDetailSheet').then((m) => ({ default: m.NovelDetailSheet })),
);

interface ReorderItemWrapperProps {
  novel: Novel;
  index: number;
  novelHistory?: ReadingHistoryItem;
  onOpenDetail: (novel: Novel) => void;
  onDeleteNovel: (id: string, e: React.MouseEvent) => void;
  textMuted: string;
  coverConfig?: NovelCoverConfig;
  currentTheme: NovelTheme;
}

const ReorderItemWrapper: React.FC<ReorderItemWrapperProps> = ({
  novel,
  index,
  novelHistory,
  onOpenDetail,
  onDeleteNovel,
  textMuted,
  coverConfig,
  currentTheme,
}) => {
  const dragControls = useDragControls();
  return (
    <Reorder.Item
      value={novel}
      dragListener={false}
      dragControls={dragControls}
      id={novel.id}
      className="w-full"
    >
      <NovelCard
        novel={novel}
        index={index}
        novelHistory={novelHistory}
        onOpenDetail={onOpenDetail}
        onDeleteNovel={onDeleteNovel}
        textMuted={textMuted}
        isManualSortMode={true}
        dragControls={dragControls}
        coverConfig={coverConfig}
        currentTheme={currentTheme}
      />
    </Reorder.Item>
  );
};

interface BookshelfProps {
  onSelectNovel: (novel: Novel) => void;
  currentTheme: NovelTheme;
  setTheme: (theme: NovelTheme) => void;
}

export const Bookshelf: React.FC<BookshelfProps> = ({ onSelectNovel, currentTheme, setTheme }) => {
  const { customNovels, novels, updateCustomNovels } = useCustomNovels();
  const { history, deleteHistoryItem } = useReadingHistory();
  const [detailNovel, setDetailNovel] = useState<Novel | null>(null);
  const [isDetailSheetOpen, setIsDetailSheetOpen] = useState(false);
  const detailSheetOpenRef = useRef(false);
  const [isNarouModalOpen, setIsNarouModalOpen] = useState(false);
  const [hasOpenedNarouModal, setHasOpenedNarouModal] = useState(false);
  const [isAddSheetOpen, setIsAddSheetOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [hasOpenedDeleteDialog, setHasOpenedDeleteDialog] = useState(false);
  const [conflictData, setConflictData] = useState<{ existing: Novel; incoming: Novel } | null>(
    null,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const settings = useReaderSettings();

  const [editingNovel, setEditingNovel] = useState<Novel | null>(null);
  const [coverConfigs, setCoverConfigs] = useState<{ [id: string]: NovelCoverConfig }>(() => {
    try {
      const stored = localStorage.getItem('novelflow_cover_configs');
      if (!stored) return {};
      const parsed = JSON.parse(stored);
      const result = CoverConfigsRecordSchema.safeParse(parsed);
      if (result.success) {
        return result.data as { [id: string]: NovelCoverConfig };
      }
      console.warn('Failed to parse cover configs:', result.error);
      return {};
    } catch {
      return {};
    }
  });

  const handleSaveCoverConfig = (config: NovelCoverConfig) => {
    if (editingNovel) {
      const updated = { ...coverConfigs, [editingNovel.id]: config };
      setCoverConfigs(updated);
      try {
        localStorage.setItem('novelflow_cover_configs', JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save cover config', e);
      }
    }
  };

  const {
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    selectedGenre,
    setSelectedGenre,
    selectedTag,
    setSelectedTag,
    selectedStatus,
    setSelectedStatus,
    sortOrder,
    setSortOrder,
    allGenres,
    allTags,
    filteredNovels,
    resetSubFilters,
    updateManualOrder,
  } = useBookshelfFilters(novels, customNovels);

  const handleReorder = (newOrderedNovels: Novel[]) => {
    const newIdOrder = newOrderedNovels.map((n) => n.id);
    updateManualOrder(newIdOrder);
  };

  const handleConflict = useCallback((existing: Novel, incoming: Novel) => {
    setConflictData({ existing, incoming });
  }, []);

  const handleResolveConflict = useCallback(
    (action: 'update' | 'create' | 'cancel') => {
      if (!conflictData) return;
      const { existing, incoming } = conflictData;

      if (action === 'update') {
        const updated = customNovels.map((n) => {
          if (n.id === existing.id) {
            return {
              ...n,
              content: incoming.content,
              charCount: incoming.charCount,
              episodesCount: incoming.episodesCount,
              lastUpdatedAt: incoming.lastUpdatedAt || new Date().toISOString(),
            };
          }
          return n;
        });
        updateCustomNovels(updated);
      } else if (action === 'create') {
        const uniqueId = `custom-${incoming.ncode || Date.now()}-${crypto.randomUUID()}`;
        const uniqueTitle = `${incoming.title} (コピー)`;
        const newNovel = {
          ...incoming,
          id: uniqueId,
          title: uniqueTitle,
        };
        updateCustomNovels([newNovel, ...customNovels]);
      }

      setConflictData(null);
    },
    [conflictData, customNovels, updateCustomNovels],
  );

  const handleUpdateNovelContent = useCallback(
    async (novel: Novel, file: File) => {
      try {
        const text = await file.text();
        const incoming = createNovelFromFile(file, text);

        const updated = customNovels.map((n) => {
          if (n.id === novel.id) {
            return {
              ...n,
              content: incoming.content,
              charCount: incoming.charCount,
              episodesCount: incoming.episodesCount,
              lastUpdatedAt: incoming.lastUpdatedAt || new Date().toISOString(),
            };
          }
          return n;
        });
        await updateCustomNovels(updated);
        alert(`「${novel.title}」の本文を更新しました。`);
      } catch (err) {
        console.error(err);
        alert('本文の更新に失敗しました。');
      }
    },
    [customNovels, updateCustomNovels],
  );

  const { handleFileUpload } = useNovelFileImport({
    customNovels,
    updateCustomNovels,
    onConflict: handleConflict,
  });

  const handleDeleteNovel = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setHasOpenedDeleteDialog(true);
    setDeleteTargetId(id);
  };

  const handleOpenDetail = (novel: Novel) => {
    detailSheetOpenRef.current = true;
    setDetailNovel(novel);
    setIsDetailSheetOpen(true);
  };

  const handleCloseDetail = () => {
    detailSheetOpenRef.current = false;
    setIsDetailSheetOpen(false);
  };

  const handleDetailExitComplete = () => {
    if (!detailSheetOpenRef.current) {
      setDetailNovel(null);
    }
  };

  const confirmDelete = () => {
    if (deleteTargetId) {
      const updatedCustom = customNovels.filter((n) => n.id !== deleteTargetId);
      updateCustomNovels(updatedCustom);
      deleteHistoryItem(deleteTargetId);
      setDeleteTargetId(null);
    }
  };

  const handleSelect = async (novel: Novel) => {
    if (!novel.content && novel.id === 'n2353hi') {
      try {
        const res = await fetch('/novels/n2353hi_sample.md');
        if (res.ok) {
          const text = await res.text();
          onSelectNovel({ ...novel, content: text });
          return;
        }
        alert('サンプルの取得に失敗しました。');
        return;
      } catch (err) {
        console.error(err);
        alert('サンプルの取得に失敗しました。');
        return;
      }
    }
    onSelectNovel(novel);
  };

  const handleCloseAddSheet = useCallback(() => {
    setIsAddSheetOpen(false);
  }, []);

  const handleChooseFile = useCallback(() => {
    setIsAddSheetOpen(false);
    fileInputRef.current?.click();
  }, []);

  const handleChooseNarou = useCallback(() => {
    setIsAddSheetOpen(false);
    setHasOpenedNarouModal(true);
    setIsNarouModalOpen(true);
  }, []);

  const latestHistory = useMemo(() => {
    const availableNovelIds = new Set(novels.map((novel) => novel.id));
    return Object.values(history)
      .filter((item) => availableNovelIds.has(item.novelId))
      .sort((a, b) => b.updatedAt - a.updatedAt)[0];
  }, [history, novels]);
  const latestNovel = useMemo(
    () => (latestHistory ? novels.find((n) => n.id === latestHistory.novelId) : null),
    [latestHistory, novels],
  );

  const isDark = isDarkTheme(currentTheme);
  const bgCol = isDark ? 'bg-[#0B0F17] text-[#FAF8F5]' : 'bg-[#F7F6F3] text-[#111111]';
  const headerBg = isDark ? 'bg-[#111111]/90' : 'bg-[#FFFFFF]/90';
  const borderCol = isDark ? 'border-[#1F242E]' : 'border-[#EAEAEA]';
  const cardBg = isDark ? 'bg-[#111111]' : 'bg-[#FFFFFF]';
  const textMuted = isDark ? 'text-neutral-400' : 'text-neutral-600';

  return (
    <div
      className={`w-full h-full flex flex-col overflow-hidden ${bgCol} transition-colors duration-300`}
    >
      <header
        className={`px-4 pt-safe pb-3 sm:px-6 border-b ${headerBg} ${borderCol} backdrop-blur-md z-30 flex items-center justify-between transition-colors`}
      >
        <div className="flex min-w-0 items-center gap-2">
          <BookOpenIcon
            className={`h-5 w-5 shrink-0 ${isDark ? 'text-[#FAF8F5]' : 'text-[#111111]'}`}
            strokeWidth={2}
          />
          <h1 className="truncate font-serif text-lg font-bold tracking-wide">
            Novel<span className="opacity-60">Flow</span>
          </h1>
        </div>
        <div className="ml-3 flex shrink-0 items-center gap-1.5">
          <AddToHomeScreen />
          <motion.button
            type="button"
            whileTap={{ scale: 0.92 }}
            whileHover={{ scale: 1.05 }}
            transition={SPRING_BOUNCY}
            onClick={() => setIsSettingsOpen(true)}
            className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
              isDark
                ? 'bg-[#1C1C1E] border-[#2C2C2E] text-neutral-300 hover:bg-[#2C2C2E]'
                : 'bg-[#FFFFFF] border-[#EAEAEA] text-neutral-700 hover:bg-[#F9F9F8]'
            }`}
            aria-label="設定"
          >
            <SettingsIcon size={16} strokeWidth={2} />
          </motion.button>
          <motion.button
            type="button"
            whileTap={{ scale: 0.94 }}
            whileHover={{ scale: 1.04 }}
            transition={SPRING_BOUNCY}
            onClick={() => setIsAddSheetOpen(true)}
            aria-label="小説を追加"
            title="小説を追加"
            className={`flex h-11 w-11 items-center justify-center rounded-xl border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${isDark ? 'border-amber-400/30 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20' : 'border-amber-600/20 bg-amber-50 text-amber-900 hover:bg-amber-100'}`}
          >
            <PlusIcon size={20} strokeWidth={2} />
          </motion.button>
        </div>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4 scroll-smooth sm:px-5">
        {latestNovel && latestHistory && (
          <section aria-label="前回の続き">
            <LatestBookmarkCard
              latestNovel={latestNovel}
              latestHistory={latestHistory}
              coverConfig={coverConfigs[latestNovel.id]}
              onSelectNovel={handleSelect}
              isDark={isDark}
              cardBg={cardBg}
              borderCol={borderCol}
              textMuted={textMuted}
            />
          </section>
        )}

        <BookshelfFilters
          filter={filter}
          setFilter={setFilter}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          resultCount={filteredNovels.length}
          selectedGenre={selectedGenre}
          setSelectedGenre={setSelectedGenre}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          sortOrder={sortOrder}
          setSortOrder={setSortOrder}
          allGenres={allGenres}
          allTags={allTags}
          resetSubFilters={resetSubFilters}
          isDark={isDark}
          borderCol={borderCol}
        />

        <div className="w-full pb-10">
          {sortOrder === 'manual' ? (
            <Reorder.Group
              axis="y"
              values={filteredNovels}
              onReorder={handleReorder}
              className="space-y-3 w-full"
            >
              {filteredNovels.map((novel, index) => (
                <ReorderItemWrapper
                  key={novel.id}
                  novel={novel}
                  index={index}
                  novelHistory={history[novel.id]}
                  onOpenDetail={handleOpenDetail}
                  onDeleteNovel={handleDeleteNovel}
                  textMuted={textMuted}
                  coverConfig={coverConfigs[novel.id]}
                  currentTheme={currentTheme}
                />
              ))}
            </Reorder.Group>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <AnimatePresence mode="popLayout">
                {filteredNovels.map((novel, index) => (
                  <NovelCard
                    key={novel.id}
                    novel={novel}
                    index={index}
                    novelHistory={history[novel.id]}
                    onOpenDetail={handleOpenDetail}
                    onDeleteNovel={handleDeleteNovel}
                    textMuted={textMuted}
                    coverConfig={coverConfigs[novel.id]}
                    currentTheme={currentTheme}
                  />
                ))}
              </AnimatePresence>
            </div>
          )}

          {filteredNovels.length === 0 && (
            <div
              className={`w-full rounded-2xl border px-5 py-8 text-center ${cardBg} ${borderCol} animate-fade-in`}
            >
              <p className="font-serif text-sm font-bold">
                {searchQuery
                  ? `「${searchQuery}」に一致する作品はありません`
                  : filter === 'my-novels'
                    ? '自分の小説を本棚に追加できます'
                    : '作品が見つかりません'}
              </p>
              <p className={`mx-auto mt-2 max-w-xs text-sm leading-relaxed ${textMuted}`}>
                {searchQuery
                  ? '別のタイトルや作者名で検索してみてください。'
                  : '条件を変えるか、小説を本棚に追加してください。'}
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className={`min-h-11 rounded-xl border px-4 text-sm font-semibold ${borderCol} ${isDark ? 'bg-white/5' : 'bg-white'}`}
                  >
                    検索をクリア
                  </button>
                )}
                {filter === 'my-novels' && !searchQuery && (
                  <button
                    type="button"
                    onClick={() => setIsAddSheetOpen(true)}
                    className={`min-h-11 rounded-xl px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${isDark ? 'bg-[#FAF8F5] text-[#111111]' : 'bg-[#111111] text-white'}`}
                  >
                    小説を追加
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        accept=".md,.txt"
        className="hidden"
      />
      <AddNovelSheet
        isOpen={isAddSheetOpen}
        isDark={isDark}
        onClose={handleCloseAddSheet}
        onChooseFile={handleChooseFile}
        onChooseNarou={handleChooseNarou}
      />
      <Suspense fallback={null}>
        {hasOpenedNarouModal && (
          <NarouImportModal
            isOpen={isNarouModalOpen}
            onClose={() => setIsNarouModalOpen(false)}
            onImported={(newNovel) => {
              updateCustomNovels([newNovel, ...customNovels]);
              setFilter('my-novels');
            }}
            isDark={isDark}
          />
        )}

        {hasOpenedDeleteDialog && (
          <ConfirmDialog
            isOpen={deleteTargetId !== null}
            title="小説の削除"
            message="この小説をライブラリから削除してもよろしいですか？（読書履歴やしおりも削除されます）"
            confirmLabel="削除する"
            isDestructive={true}
            isDark={isDark}
            onConfirm={confirmDelete}
            onCancel={() => setDeleteTargetId(null)}
          />
        )}

        <AnimatePresence>
          {detailNovel && (
            <motion.div
              key="detail-sheet-shell"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              className="fixed inset-0 z-50"
            >
              <motion.div
                key="detail-backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.4 }}
                exit={{ opacity: 0 }}
                onClick={handleCloseDetail}
                className="fixed inset-0 bg-black z-45 pointer-events-auto"
              />
              <NovelDetailSheet
                key="detail-sheet"
                novel={detailNovel}
                isOpen={isDetailSheetOpen}
                onClose={handleCloseDetail}
                onExitComplete={handleDetailExitComplete}
                onRead={handleSelect}
                isDark={isDark}
                novelHistory={history[detailNovel.id]}
                coverConfig={coverConfigs[detailNovel.id]}
                onOpenCoverEditor={(n) => {
                  handleCloseDetail();
                  setEditingNovel(n);
                }}
                onUpdateContent={handleUpdateNovelContent}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {editingNovel !== null && (
            <CoverEditorModal
              key={editingNovel.id}
              isOpen={editingNovel !== null}
              onClose={() => setEditingNovel(null)}
              novel={editingNovel}
              currentConfig={coverConfigs[editingNovel.id] ?? null}
              onSave={handleSaveCoverConfig}
              isDark={isDark}
            />
          )}
        </AnimatePresence>
      </Suspense>

      <ReaderSettings
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        defaultTab="common"
        currentTheme={currentTheme}
        setTheme={setTheme}
        customNovels={customNovels}
        updateCustomNovels={updateCustomNovels}
        {...settings}
      />

      <AnimatePresence>
        {conflictData && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setConflictData(null)}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`relative w-full max-w-sm rounded-2xl border ${borderCol} ${cardBg} shadow-2xl p-6`}
            >
              <h3 className="text-lg font-bold font-serif mb-2 pr-6">同じ小説が本棚にあります</h3>
              <p
                className={`text-xs mb-6 leading-relaxed ${isDark ? 'text-neutral-300' : 'text-neutral-600'}`}
              >
                「{conflictData.existing.title}
                」はすでに本棚に存在します。どのようなアクションを行いますか？
              </p>
              <div className="space-y-2.5">
                <button
                  type="button"
                  onClick={() => handleResolveConflict('update')}
                  className="w-full py-3 rounded-lg text-xs font-bold transition-all active:scale-[0.98] bg-amber-500 text-white hover:bg-amber-600"
                >
                  本文のみをアップデート (推奨)
                  <span className="block text-[9px] font-normal opacity-90 mt-0.5">
                    表紙の設定や読書しおりの位置は維持されます
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => handleResolveConflict('create')}
                  className={`w-full py-3 rounded-lg text-xs font-bold transition-all active:scale-[0.98] border ${
                    isDark
                      ? 'border-neutral-700 bg-neutral-900/60 hover:bg-neutral-800 text-neutral-200'
                      : 'border-neutral-300 bg-neutral-100 hover:bg-neutral-200 text-neutral-800'
                  }`}
                >
                  別小説として新規追加 (コピー)
                </button>
                <button
                  type="button"
                  onClick={() => handleResolveConflict('cancel')}
                  className={`w-full py-2.5 rounded-lg text-xs font-bold transition-all active:scale-[0.98] ${
                    isDark
                      ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  キャンセル
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
